import type { fabric } from 'fabric';
import type { MatchBundle } from './apply-match-layout';
import type { LayoutScopeKey } from './authored-bundles-bbox';
import { objectBbox } from './authored-bundles-bbox';

/**
 * Group live canvas objects by `data[scopeKey]` (positive integers only) —
 * `matchNumber` for match templates, `teamNumber` (table position) for
 * standings templates. Each group becomes a `MatchBundle` with its members and
 * aggregate bbox. Callers must discard any ActiveSelection before invoking so
 * member `left/top` are canvas-absolute.
 */
export function collectSurvivingBundles(
  objects: readonly fabric.Object[],
  scopeKey: LayoutScopeKey,
): MatchBundle[] {
  const bySlot = new Map<number, fabric.Object[]>();
  for (const obj of objects) {
    const n = (obj.data as Record<string, number | undefined> | undefined)?.[scopeKey];
    if (typeof n !== 'number' || n < 1) continue;
    if (!Number.isInteger(n)) continue;
    let members = bySlot.get(n);
    if (!members) {
      members = [];
      bySlot.set(n, members);
    }
    members.push(obj);
  }
  const bundles: MatchBundle[] = [];
  bySlot.forEach((members, slot) => {
    const rects = members.map((m: fabric.Object) =>
      objectBbox(m as unknown as Parameters<typeof objectBbox>[0]),
    );
    const bbox = unionRectsRequired(rects);
    bundles.push({ slot, members, bbox });
  });
  return bundles;
}

function unionRectsRequired(
  rects: readonly { left: number; top: number; width: number; height: number }[],
): { left: number; top: number; width: number; height: number } {
  let minL = Infinity;
  let minT = Infinity;
  let maxR = -Infinity;
  let maxB = -Infinity;
  for (const r of rects) {
    if (r.left < minL) minL = r.left;
    if (r.top < minT) minT = r.top;
    if (r.left + r.width > maxR) maxR = r.left + r.width;
    if (r.top + r.height > maxB) maxB = r.top + r.height;
  }
  return { left: minL, top: minT, width: maxR - minL, height: maxB - minT };
}
